import { formatMoney } from '@/lib/money';
import { enabledCustomFees, type CustomFeeLike } from './custom-fee-display';
import { formatPercentage } from './utils';

export interface PricingItem {
  id: string;
  nameKey?: string;
  feeDescription?: string;
  isRecurring?: boolean;
  price: string;
  percentage: string;
  enabled: boolean;
}

const toCustomFeePricingItem = (fee: CustomFeeLike, currency: string | undefined): PricingItem => ({
  id: fee.id,
  feeDescription: fee.description,
  isRecurring: fee.isRecurring,
  price: formatMoney(fee.amountCents, currency),
  percentage: '',
  enabled: true,
});

export function buildPricingCategories(accountPlan: any): Record<string, PricingItem[]> | null {
  if (!accountPlan) return null;

  const currency = accountPlan.billingCurrency?.toUpperCase();
  const additionalServices = enabledCustomFees(accountPlan.customFees as CustomFeeLike[] | undefined).map(
    (fee) => toCustomFeePricingItem(fee, currency),
  );

  return {
    core: [
      {
        id: 'approved',
        nameKey: 'pricing.items.approvedTransactions',
        price: formatMoney(accountPlan.amountPerSuccessTransaction, currency),
        percentage: formatPercentage(accountPlan.pctPerSuccessTransaction || 0),
        enabled: accountPlan.enableChargeForSuccessTransaction,
      },
      {
        id: 'subclub-transaction',
        nameKey: 'pricing.items.subclubTransactions',
        price: formatMoney(accountPlan.amountPerSubclubTransaction, currency),
        percentage: formatPercentage(accountPlan.pctPerSubclubTransaction || 0),
        enabled: accountPlan.enableChargeForSubclubTransaction,
      },
      {
        id: 'declined',
        nameKey: 'pricing.items.declinedTransactions',
        price: formatMoney(accountPlan.amountPerDeclinedTransaction, currency),
        percentage: formatPercentage(accountPlan.pctPerDeclinedTransaction || 0),
        enabled: accountPlan.enableChargeForDeclinedTransaction,
      },
      {
        id: 'refund',
        nameKey: 'pricing.items.refundProcessing',
        price: formatMoney(accountPlan.amountPerRefund, currency),
        percentage: formatPercentage(accountPlan.pctPerRefund || 0),
        enabled: accountPlan.enableChargeForRefund,
      },
      {
        id: 'chargeback',
        nameKey: 'pricing.items.chargebackAlerts',
        price: formatMoney(accountPlan.amountPerChargeBack, currency),
        percentage: formatPercentage(accountPlan.pctPerChargeBack || 0),
        enabled: accountPlan.enableChargeForChargeBack,
      },
      {
        id: '3ds',
        nameKey: 'pricing.items.threeDsVerifications',
        price: formatMoney(accountPlan.amountPer3dsVerification, currency),
        percentage: formatPercentage(accountPlan.pctPer3dsVerification || 0),
        enabled: accountPlan.enableChargeFor3dsVerification,
      },
      {
        id: 'late',
        nameKey: 'pricing.items.latePayments',
        price: formatMoney(accountPlan.amountPerLatePayment, currency),
        percentage: formatPercentage(accountPlan.pctPerLatePayment || 0),
        enabled: accountPlan.enableChargeForLatePayment,
      },
    ],
    growth: [
      {
        id: 'auto-retry',
        nameKey: 'pricing.items.automaticRetries',
        price: '',
        percentage: formatPercentage(accountPlan.pctPerAutomaticRetry || 0),
        enabled: accountPlan.enableChargeForAutomaticRetry,
      },
    ],
    revenueBoosters: [
      {
        id: 'upsell',
        nameKey: 'pricing.items.orderBumps',
        price: '',
        percentage: formatPercentage(accountPlan.pctPerUpsell || 0),
        enabled: accountPlan.enableChargeForUpsell,
      },
      {
        id: 'subscription',
        nameKey: 'pricing.items.subscriptions',
        price: '',
        percentage: formatPercentage(accountPlan.pctPerSubscription || 0),
        enabled: accountPlan.enableChargeForSubscription,
      },
    ],
    wallet: [
      {
        id: 'apple-pay',
        nameKey: 'pricing.items.applePay',
        price: '',
        percentage: formatPercentage(accountPlan.pctPerApplePay || 0),
        enabled: accountPlan.enableChargeForApplePay,
      },
      {
        id: 'google-pay',
        nameKey: 'pricing.items.googlePay',
        price: '',
        percentage: formatPercentage(accountPlan.pctPerGooglePay || 0),
        enabled: accountPlan.enableChargeForGooglePay,
      },
      {
        id: 'apple-pay-domain',
        nameKey: 'pricing.items.applePayDomain',
        price: formatMoney(accountPlan.amountPerApplePayDomain, currency),
        percentage: '',
        enabled: accountPlan.enableChargeForApplePayDomain,
      },
    ],
    customDomains: [
      {
        id: 'custom-domain',
        nameKey: 'pricing.items.customDomain',
        price: formatMoney(accountPlan.amountPerCustomDomain, currency),
        percentage: '',
        enabled: accountPlan.enableChargeForCustomDomain,
      },
    ],
    ...(additionalServices.length > 0 ? { additionalServices } : {}),
  };
}
